import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, Chip, CircularProgress, Paper, Snackbar, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloneIcon from "@mui/icons-material/ContentCopyOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import ViewIcon from "@mui/icons-material/VisibilityOutlined";
import CancelIcon from "@mui/icons-material/CancelOutlined";
import ApproveIcon from "@mui/icons-material/CheckCircleOutline";
import UndoIcon from "@mui/icons-material/UndoOutlined";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import ExtractIcon from "@mui/icons-material/DownloadOutlined";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import { tokens } from "../../../../../theme";
import ConnString from "../../../../../ConnString";
import { useNavigate } from "react-router-dom";

const API = ConnString.connstring;
const MODULE_ID = "PROCPO";
const FILTER_KEYS = {
  branch: "purchaseOrder_branch",
  dateFrom: "purchaseOrder_dateFrom",
  dateTo: "purchaseOrder_dateTo",
};
const ACTIONS = [
  ["NEW", <AddIcon />], ["CLONE", <CloneIcon />], ["EDIT", <EditIcon />],
  ["VIEW", <ViewIcon />], ["CANCEL", <CancelIcon />], ["APPROVE", <ApproveIcon />],
  ["UNAPPROVE", <UndoIcon />], ["PRINT", <PrintIcon />], ["EXTRACT", <ExtractIcon />],
];
const isValidDate = (date) => date instanceof Date && !Number.isNaN(date.getTime());
const apiDate = (date) => isValidDate(date) ? date.toLocaleDateString("en-US") : "";
const statusOf = (row) => String(row?.Status || "").trim().toUpperCase();
const csvCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
const storedDate = (key, fallback) => {
  const value = sessionStorage.getItem(key);
  const date = value ? new Date(value) : fallback;
  return Number.isNaN(date.getTime()) ? fallback : date;
};

const PurchaseOrderList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const darkMode = theme.palette.mode === "dark";
  const gridHeaderBackground = darkMode ? colors.blueAccent[700] : colors.blueAccent[200];
  const gridHeaderText = darkMode ? "#ffffff" : "#000000";
  const compID = sessionStorage.getItem("compID");
  const userID = sessionStorage.getItem("userID");
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedIDs, setSelectedIDs] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branch, setBranch] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => storedDate(FILTER_KEYS.dateFrom, new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [dateTo, setDateTo] = useState(() => storedDate(FILTER_KEYS.dateTo, new Date()));
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState({ open: false, severity: "info", message: "" });

  const selectedRows = useMemo(() => rows.filter((row) => selectedIDs.includes(String(row.__id))), [rows, selectedIDs]);
  const counts = useMemo(() => rows.reduce((value, row) => {
    const status = statusOf(row);
    value.total += 1;
    if (["APPROVED", "CLOSED", "PARTIAL"].includes(status)) value.approved += 1;
    else if (status === "CANCELLED") value.cancelled += 1;
    else value.open += 1;
    return value;
  }, { total: 0, approved: 0, cancelled: 0, open: 0 }), [rows]);

  const notify = (message, severity = "info") => setNotice({ open: true, message, severity });
  const changeDate = (setter, storageKey, value) => { setter(value); if (isValidDate(value)) sessionStorage.setItem(storageKey, value.toISOString()); };
  const can = (action) => permissions.includes(action);

  const fetchColumns = useCallback(async () => {
    const response = await fetch(`${API}/api/TranslistHeader`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ compID, moduleID: MODULE_ID, transType: MODULE_ID, type: "ColumnNames" }),
    });
    const data = await response.json();
    setColumns((data.columns || []).map((field) => ({
      field, headerName: field.replace(/([a-z])([A-Z])/g, "$1 $2"), minWidth: Math.max(120, field.length * 10),
      flex: ["Particulars", "SupplierName", "TradeName"].includes(field) ? 1 : undefined,
      renderCell: field === "Status" ? ({ value }) => {
        const status = String(value || "New");
        return <Chip size="small" label={status} color={status.toUpperCase() === "CANCELLED" ? "error" : status.toUpperCase() === "APPROVED" ? "success" : "warning"} variant="outlined" />;
      } : undefined,
    })));
  }, [compID]);

  const fetchRows = useCallback(async (selectedBranch = branch, from = dateFrom, to = dateTo) => {
    if (!selectedBranch) return;
    if (!isValidDate(from) || !isValidDate(to)) return notify("Enter a valid From and To date.", "warning");
    setLoading(true);
    try {
      const branchCode = selectedBranch.BranchCode || selectedBranch.id;
      const response = await fetch(`${API}/api/TranslistData`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ compID, moduleID: MODULE_ID, transType: MODULE_ID, dateFrom: apiDate(from), dateTo: apiDate(to), filter: `and Branch = '${String(branchCode).replace(/'/g, "''")}'` }),
      });
      const data = await response.json();
      setRows((Array.isArray(data.columns) ? data.columns : []).map((row, index) => ({ ...row, __id: String(row.RecID ?? `${row.DocNo}-${index}`) })));
      setSelectedIDs([]);
    } catch (error) { notify(error.message || "Unable to load purchase orders.", "error"); }
    finally { setLoading(false); }
  }, [branch, compID, dateFrom, dateTo]);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      try {
        const [branchResponse, accessResponse] = await Promise.all([
          fetch(`${API}/api/userBranchAccess`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ companyID: compID, currentuserID: userID }) }),
          fetch(`${API}/api/inventory/purchase-orders/access`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ compID, userID }) }),
          fetchColumns(),
        ]);
        const branchData = await branchResponse.json();
        const accessData = await accessResponse.json();
        const options = Array.isArray(branchData.options) ? branchData.options : [];
        setBranches(options);
        setPermissions((accessData.permissions || []).map((permission) => String(permission).toUpperCase()));
        const savedBranchCode = sessionStorage.getItem(FILTER_KEYS.branch);
        const restoredBranch = options.find((option) => String(option.BranchCode || option.id) === savedBranchCode) || options[0];
        if (restoredBranch) { setBranch(restoredBranch); await fetchRows(restoredBranch, dateFrom, dateTo); }
      } catch (error) { notify(error.message || "Unable to initialize Purchase Orders.", "error"); }
      finally { setLoading(false); }
    };
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requireOne = () => {
    if (selectedRows.length !== 1) { notify("Select exactly one purchase order.", "warning"); return null; }
    return selectedRows[0];
  };
  const changeStatus = async (action) => {
    if (!selectedRows.length) return notify("Select at least one purchase order.", "warning");
    const invalid = selectedRows.find((row) => {
      const status = statusOf(row);
      if (action === "APPROVE") return ["APPROVED", "CANCELLED", "CLOSED", "PARTIAL"].includes(status);
      if (action === "UNAPPROVE") return status !== "APPROVED";
      return ["APPROVED", "CANCELLED", "CLOSED", "PARTIAL"].includes(status);
    });
    if (invalid) return notify(`${invalid.DocNo || "Selected record"} cannot be updated from status ${invalid.Status}.`, "warning");
    if (!window.confirm(`${action} ${selectedRows.length} selected purchase order(s)?`)) return;
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/inventory/purchase-orders/status`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ compID, userID, action, recIDs: selectedRows.map((row) => row.RecID) }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message);
      notify(data.message, "success");
      await fetchRows();
    } catch (error) { notify(error.message || "Status update failed.", "error"); }
    finally { setLoading(false); }
  };
  const extract = () => {
    if (!rows.length) return notify("There are no purchase orders to extract.", "warning");
    const fields = columns.map((column) => column.field);
    const csv = [fields.map(csvCell).join(","), ...rows.map((row) => fields.map((field) => csvCell(row[field])).join(","))].join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    link.download = `Purchase_Order_Translist_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click(); URL.revokeObjectURL(link.href);
  };
  const print = () => {
    const row = requireOne(); if (!row) return;
    const popup = window.open("", "_blank", "width=1000,height=700");
    if (!popup) return notify("Allow pop-ups to print.", "warning");
    const detail = columns.map(({ field, headerName }) => `<tr><th>${headerName}</th><td>${String(row[field] ?? "")}</td></tr>`).join("");
    popup.document.write(`<html><head><title>Purchase Order ${row.DocNo || ""}</title><style>body{font-family:Arial;padding:32px}table{border-collapse:collapse;width:100%}th,td{padding:9px;border-bottom:1px solid #ddd;text-align:left}th{width:30%;background:#f5f5f5}</style></head><body><h1>Purchase Order ${row.DocNo || ""}</h1><table>${detail}</table><script>window.onload=()=>window.print()</script></body></html>`);
    popup.document.close();
  };
  const handleAction = (action) => {
    if (["APPROVE", "UNAPPROVE", "CANCEL"].includes(action)) return changeStatus(action);
    if (action === "EXTRACT") return extract();
    if (action === "PRINT") return print();
    if (action === "NEW") return navigate(`/App/PurchaseOrderTransaction?mode=new&branch=${encodeURIComponent(branch?.BranchCode || branch?.id || "")}`);
    const row = requireOne(); if (!row) return;
    if (action === "EDIT" && ["APPROVED", "CANCELLED"].includes(statusOf(row))) return notify("Approved or cancelled purchase orders cannot be edited.", "warning");
    const formMode = action.toLowerCase();
    navigate(`/App/PurchaseOrderTransaction?mode=${formMode}&docNo=${encodeURIComponent(row.DocNo || row.Docnumber || "")}&branch=${encodeURIComponent(row.Branch || "")}`);
  };

  return (
    <Box m={{ xs: 1.5, md: 3 }} display="flex" flexDirection="column" height="calc(100vh - 95px)" gap={2}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
        <Box><Typography variant="h3" fontWeight={800}>Purchase Orders</Typography><Typography color="text.secondary">{counts.total} records · {counts.open} open · {counts.approved} approved · {counts.cancelled} cancelled</Typography></Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Autocomplete size="small" sx={{ minWidth: 210 }} options={branches} value={branch} getOptionLabel={(option) => option?.LocName || option?.BranchName || option?.BranchCode || ""} isOptionEqualToValue={(a, b) => (a.BranchCode || a.id) === (b.BranchCode || b.id)} onChange={(_, value) => { setBranch(value); if (value) { sessionStorage.setItem(FILTER_KEYS.branch, String(value.BranchCode || value.id)); fetchRows(value); } }} renderInput={(params) => <TextField {...params} label="Branch" />} />
          <DatePicker label="From" value={dateFrom} onChange={(value) => changeDate(setDateFrom, FILTER_KEYS.dateFrom, value)} slotProps={{ textField: { size: "small" } }} />
          <DatePicker label="To" value={dateTo} onChange={(value) => changeDate(setDateTo, FILTER_KEYS.dateTo, value)} slotProps={{ textField: { size: "small" } }} />
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => { if (branch) sessionStorage.setItem(FILTER_KEYS.branch, String(branch.BranchCode || branch.id)); fetchRows(); }}>Refresh</Button>
        </Stack>
      </Stack>
      <Paper elevation={0} sx={{ p: 1.25, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" gap={1} flexWrap="wrap">{ACTIONS.map(([action, icon]) => (
          <Tooltip key={action} title={action !== "NEW" && !can(action) ? `No ${action.toLowerCase()} access` : action}><span><Button size="small" variant={["NEW", "APPROVE"].includes(action) ? "contained" : "outlined"} color={["CANCEL", "UNAPPROVE"].includes(action) ? "error" : action === "APPROVE" ? "success" : "primary"} startIcon={icon} disabled={loading || (action !== "NEW" && !can(action))} onClick={() => handleAction(action)} sx={{ minWidth: 108, fontWeight: 700, ...(darkMode && { color: "common.white", borderColor: "currentColor" }), "& .MuiButton-startIcon": { color: "inherit" }, "&.Mui-disabled": { color: "action.disabled" } }}>{action}</Button></span></Tooltip>
        ))}</Stack>
      </Paper>
      <Paper elevation={0} sx={{ flex: 1, minHeight: 320, borderRadius: 3, overflow: "hidden", border: `1px solid ${theme.palette.divider}` }}>
        <DataGrid rows={rows} columns={columns} getRowId={(row) => row.__id} checkboxSelection disableRowSelectionOnClick loading={loading} density="compact" pageSizeOptions={[25, 50, 100]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} onRowSelectionModelChange={(model) => setSelectedIDs(Array.from(model?.ids || model || []).map(String))} getRowClassName={({ row }) => `po-${statusOf(row).toLowerCase()}`} slots={{ loadingOverlay: () => <Stack height="100%" alignItems="center" justifyContent="center"><CircularProgress /></Stack> }} sx={{ border: 0, "& .MuiDataGrid-columnHeaders": { bgcolor: gridHeaderBackground, color: gridHeaderText }, "& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeader .MuiSvgIcon-root, & .MuiDataGrid-menuIconButton": { color: `${gridHeaderText} !important` }, "& .po-cancelled": { opacity: 0.65 }, "& .po-approved": { bgcolor: darkMode ? "rgba(34,197,94,.08)" : "rgba(34,197,94,.05)" } }} />
      </Paper>
      <Snackbar open={notice.open} autoHideDuration={5000} onClose={() => setNotice((value) => ({ ...value, open: false }))}><Alert severity={notice.severity} onClose={() => setNotice((value) => ({ ...value, open: false }))}>{notice.message}</Alert></Snackbar>
    </Box>
  );
};

export default PurchaseOrderList;
